import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import axiosInstance from '../../lib/axios';
import type { Supplier } from '../../types';
import { palette } from '../../theme/colors';

// Types for purchase history API response
interface BulkRestockOrder {
  id: string;
  orderNumber: string;
  status: string;
  totalAmount: number;
  createdAt: string;
  location?: { name: string };
  items?: Array<{
    id: string;
    productName: string;
    quantity: number;
    unitCost: number;
  }>;
}

interface SupplierExpense {
  id: string;
  category: string;
  amount: number;
  expenseDate: string;
  description?: string;
  location?: { name: string };
  metadata?: { orderId?: string; orderNumber?: string };
}

interface InventoryBatch {
  id: string;
  batchNumber: string;
  quantityReceived: number;
  quantityRemaining: number;
  unitCost: number;
  receivedDate: string;
  createdAt: string;
  product?: { name: string; sku?: string };
  location?: { name: string };
}

interface PurchaseHistoryStats {
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: string | null;
  totalExpenses: number;
  totalBatches: number;
}

interface SupplierPurchaseHistory {
  stats: PurchaseHistoryStats;
  orders: BulkRestockOrder[];
  expenses: SupplierExpense[];
  batches: InventoryBatch[];
}

const fetchSupplier = async (id: string): Promise<Supplier> => {
  const response = await axiosInstance.get(`/suppliers/${id}`);
  return response.data;
};

const fetchPurchaseHistory = async (id: string): Promise<SupplierPurchaseHistory> => {
  const response = await axiosInstance.get(`/suppliers/${id}/purchase-history`);
  return response.data;
};

const getPaymentTermsLabel = (terms: string) => {
  const labels: Record<string, string> = {
    net_7: 'Net 7 days',
    net_15: 'Net 15 days',
    net_30: 'Net 30 days',
    net_60: 'Net 60 days',
    net_90: 'Net 90 days',
    due_on_receipt: 'Due on Receipt',
    custom: 'Custom',
  };
  return labels[terms] || terms;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
    case 'delivered':
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'confirmed':
    case 'approved':
      return 'info';
    case 'cancelled':
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const formatCurrency = (amount: number, currency: string = 'DZD') => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount);
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`supplier-tabpanel-${index}`}
      aria-labelledby={`supplier-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const SupplierShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { data: supplier, isLoading, error } = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => fetchSupplier(id!),
    enabled: !!id,
  });

  const { data: purchaseHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['supplier-purchase-history', id],
    queryFn: () => fetchPurchaseHistory(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/suppliers')}>
            <ArrowBackIcon />
          </IconButton>
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !supplier) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/suppliers')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Supplier Not Found</Typography>
        </Box>
        <Card>
          <CardContent>
            <Typography color="error">
              Unable to load supplier details. The supplier may not exist or you may not have permission to view them.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/app/suppliers')}
              sx={{ mt: 2 }}
            >
              Back to Suppliers
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/app/suppliers')}>
          <ArrowBackIcon />
        </IconButton>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              bgcolor: palette.primary,
              width: 56,
              height: 56,
              fontSize: '1.5rem',
            }}
          >
            {supplier.name?.[0]?.toUpperCase() || <BusinessIcon />}
          </Avatar>
          <Box>
            <Typography variant="h4">{supplier.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Code: {supplier.code} • Supplier since {new Date(supplier.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
        {!supplier.isActive && (
          <Chip label="Inactive" color="error" sx={{ ml: 'auto' }} />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Supplier Info */}
        <Grid item xs={12} md={6}>
          {/* Contact Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {supplier.contactName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BusinessIcon sx={{ color: palette.textMuted }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Contact Person
                      </Typography>
                      <Typography>{supplier.contactName}</Typography>
                    </Box>
                  </Box>
                )}
                {supplier.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon sx={{ color: palette.textMuted }} />
                    <Typography>{supplier.email}</Typography>
                  </Box>
                )}
                {supplier.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ color: palette.textMuted }} />
                    <Typography>{supplier.phone}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Address */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>
              {supplier.address ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <LocationOnIcon sx={{ color: palette.textMuted, mt: 0.5 }} />
                  <Typography>{supplier.address}</Typography>
                </Box>
              ) : (
                <Typography color="text.secondary">No address provided</Typography>
              )}
            </CardContent>
          </Card>

          {/* Payment & Tax Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment & Tax Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PaymentIcon sx={{ color: palette.textMuted }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payment Terms
                    </Typography>
                    <Typography>
                      {getPaymentTermsLabel(supplier.defaultPaymentTerms)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                    Currency:
                  </Typography>
                  <Typography>{supplier.currency}</Typography>
                </Box>
                {supplier.taxId && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                      Tax ID:
                    </Typography>
                    <Typography>{supplier.taxId}</Typography>
                  </Box>
                )}
              </Box>
              {supplier.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: palette.surfaceElevated }}
                  >
                    <Typography variant="body2">{supplier.notes}</Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Stats and Purchase History Tabs */}
        <Grid item xs={12} md={6}>
          {/* Statistics Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistics
              </Typography>
              {isHistoryLoading ? (
                <Skeleton variant="rectangular" height={100} />
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 2,
                    }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: palette.surfaceElevated,
                      }}
                    >
                      <ShoppingCartIcon sx={{ fontSize: 32, color: palette.primary, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {purchaseHistory?.stats.totalOrders || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Orders
                      </Typography>
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: palette.surfaceElevated,
                      }}
                    >
                      <LocalOfferIcon sx={{ fontSize: 32, color: palette.success, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(purchaseHistory?.stats.totalSpent || 0, supplier.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Spent
                      </Typography>
                    </Paper>
                  </Box>
                  {purchaseHistory?.stats.lastOrderDate && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Last order: {new Date(purchaseHistory.stats.lastOrderDate).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Purchase History */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Purchase History
              </Typography>

              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                  <Tab
                    icon={<ShoppingCartIcon />}
                    label={`Orders (${purchaseHistory?.stats.totalOrders || 0})`}
                  />
                  <Tab
                    icon={<ReceiptIcon />}
                    label={`Expenses (${purchaseHistory?.stats.totalExpenses || 0})`}
                  />
                  <Tab
                    icon={<InventoryIcon />}
                    label={`Batches (${purchaseHistory?.stats.totalBatches || 0})`}
                  />
                </Tabs>
              </Box>

              {/* Bulk Restock Orders Tab */}
              <TabPanel value={activeTab} index={0}>
                {isHistoryLoading ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : purchaseHistory?.orders && purchaseHistory.orders.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Order #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Items</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchaseHistory.orders.slice(0, 10).map((order) => (
                          <TableRow
                            key={order.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/app/restocks/${order.id}`)}
                          >
                            <TableCell>{order.orderNumber}</TableCell>
                            <TableCell>
                              {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.status}
                                size="small"
                                color={getStatusColor(order.status) as 'success' | 'warning' | 'info' | 'error' | 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {order.items?.length || 0} items
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(order.totalAmount, supplier.currency)}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/app/restocks/${order.id}`);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: 'center',
                      bgcolor: palette.surfaceElevated,
                      borderRadius: 2,
                    }}
                  >
                    <ShoppingCartIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
                    <Typography color="text.secondary">
                      No restock orders yet
                    </Typography>
                  </Box>
                )}
                {purchaseHistory?.orders && purchaseHistory.orders.length > 10 && (
                  <Box textAlign="center" mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      Showing 10 of {purchaseHistory.stats.totalOrders} orders
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Expenses Tab */}
              <TabPanel value={activeTab} index={1}>
                {isHistoryLoading ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : purchaseHistory?.expenses && purchaseHistory.expenses.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Order #</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchaseHistory.expenses.slice(0, 10).map((expense) => (
                          <TableRow key={expense.id} hover>
                            <TableCell>
                              {new Date(expense.expenseDate).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={expense.category}
                                size="small"
                                color={expense.category === 'restock' ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {expense.description || '-'}
                            </TableCell>
                            <TableCell>
                              {expense.metadata?.orderNumber ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => navigate(`/app/restocks/${expense.metadata?.orderId}`)}
                                >
                                  {expense.metadata.orderNumber}
                                </Button>
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(expense.amount, supplier.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: 'center',
                      bgcolor: palette.surfaceElevated,
                      borderRadius: 2,
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
                    <Typography color="text.secondary">
                      No expenses yet
                    </Typography>
                  </Box>
                )}
                {purchaseHistory?.expenses && purchaseHistory.expenses.length > 10 && (
                  <Box textAlign="center" mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      Showing 10 of {purchaseHistory.stats.totalExpenses} expenses
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Inventory Batches Tab */}
              <TabPanel value={activeTab} index={2}>
                {isHistoryLoading ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : purchaseHistory?.batches && purchaseHistory.batches.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Batch #</TableCell>
                          <TableCell>Product</TableCell>
                          <TableCell>Received</TableCell>
                          <TableCell>Remaining</TableCell>
                          <TableCell align="right">Unit Cost</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchaseHistory.batches.slice(0, 10).map((batch) => (
                          <TableRow key={batch.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {batch.batchNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">
                                  {batch.product?.name || 'Unknown Product'}
                                </Typography>
                                {batch.product?.sku && (
                                  <Typography variant="caption" color="text.secondary">
                                    {batch.product.sku}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{batch.quantityReceived}</TableCell>
                            <TableCell>
                              <Chip
                                label={batch.quantityRemaining}
                                size="small"
                                color={batch.quantityRemaining > 0 ? 'success' : 'default'}
                                variant={batch.quantityRemaining > 0 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(batch.unitCost, supplier.currency)}
                            </TableCell>
                            <TableCell>
                              {new Date(batch.receivedDate).toLocaleDateString('fr-FR')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: 'center',
                      bgcolor: palette.surfaceElevated,
                      borderRadius: 2,
                    }}
                  >
                    <InventoryIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
                    <Typography color="text.secondary">
                      No inventory batches yet
                    </Typography>
                  </Box>
                )}
                {purchaseHistory?.batches && purchaseHistory.batches.length > 10 && (
                  <Box textAlign="center" mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      Showing 10 of {purchaseHistory.stats.totalBatches} batches
                    </Typography>
                  </Box>
                )}
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
